/**
 * Vertragstexte werden von Agenturen geschrieben und Marken wie Creatorn angezeigt, auch auf der öffentlichen
 * Unterschriftsseite. Bevor HTML in die Seite kommt, bleibt nur die feste Positivliste der Fachlogik übrig
 * (CONTRACT_MARKUP in packages/domain/src/contract-markup.ts): keine Skripte, keine Ereignis-Attribute, keine Links oder
 * Bilder von außen, keine Formulare und nur die Klassen des Vertragstexts (VB-25). Dieselbe Liste prüft die Datenbank beim
 * Aktivieren einer Vorlage und beim Versand und lehnt alles andere ab; was hier noch ankommt, ist ein Entwurf.
 * Klassen, die Text verstecken könnten (sr-only, tk-mobile-only …), fallen hier weg: Der Text wird sichtbar statt versteckt.
 */
import { CONTRACT_MARKUP, markupListOf } from "@talentkontor/domain";

const ALLOWED_TAGS = new Set(CONTRACT_MARKUP.tags);
const VOID_TAGS = new Set(CONTRACT_MARKUP.voidTags);
/** Inhalt wird mitsamt Tag verworfen (nie Vertragstext; der Server lässt solche Elemente gar nicht erst zu) */
const DROP_WITH_CONTENT = new Set(["script", "style", "iframe", "object", "embed", "noscript", "template", "svg", "math", "textarea", "select", "title", "head"]);
const KEY = /^[a-z][a-z0-9_]{0,63}$/;
const SPAN = /^[1-9][0-9]{0,2}$/;

function escapeAttr(v: string): string {
  return v.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Attribute nach der Positivliste: genau eine erlaubte Klasse, dazu die Attribute des Elements; class zuerst. */
function cleanAttrs(tag: string, raw: string): string {
  const values = new Map<string, string>();
  const re = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)(?:\s*=\s*("([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) {
    const name = m[1]!.toLowerCase();
    if (!values.has(name)) values.set(name, m[3] ?? m[4] ?? m[5] ?? "");
  }
  const cls = (values.get("class") ?? "").trim();
  const classOk = cls !== "" && markupListOf(CONTRACT_MARKUP.classes, cls).includes(tag);
  const attr = (name: string, value: string) => ` ${name}="${escapeAttr(value)}"`;
  const out: string[] = classOk ? [attr("class", cls)] : [];
  for (const name of markupListOf(CONTRACT_MARKUP.attributes, tag)) {
    const value = values.get(name);
    if (value === undefined) continue;
    const ok = name === "data-role" ? classOk && cls === "tk-signature" && CONTRACT_MARKUP.roles.includes(value)
      : name === "data-var" ? classOk && cls === "tk-fill" && KEY.test(value)
      : name === "colspan" || name === "rowspan" ? SPAN.test(value)
      : name === "scope" ? CONTRACT_MARKUP.scopes.includes(value)
      : false;
    if (ok) out.push(attr(name, value));
  }
  return out.join("");
}

export function sanitizeHtml(html: string | null | undefined): string {
  const src = html ?? "";
  let out = "";
  let i = 0;
  // Anführungszeichen beachten: ein „>“ in einem Attributwert beendet das Tag nicht
  const tagRe = /^<(\/?)([a-zA-Z][a-zA-Z0-9]*)((?:[^>"']|"[^"]*"|'[^']*')*)>/;
  while (i < src.length) {
    const lt = src.indexOf("<", i);
    if (lt < 0) { out += src.slice(i); break; }
    out += src.slice(i, lt);
    const rest = src.slice(lt);
    if (rest.startsWith("<!--")) {
      const end = src.indexOf("-->", lt + 4);
      i = end < 0 ? src.length : end + 3;
      continue;
    }
    const m = tagRe.exec(rest);
    if (!m) { out += "&lt;"; i = lt + 1; continue; }
    const closing = m[1] === "/";
    const tag = m[2]!.toLowerCase();
    if (DROP_WITH_CONTENT.has(tag)) {
      if (!closing && !/\/\s*$/.test(m[3] ?? "")) {
        const close = new RegExp(`</${tag}\\s*>`, "i").exec(src.slice(lt + m[0].length));
        i = close ? lt + m[0].length + close.index + close[0].length : src.length;
      } else i = lt + m[0].length;
      continue;
    }
    if (ALLOWED_TAGS.has(tag)) {
      // „</br>“ ist im Browser (und im PDF) ein Zeilenumbruch; andere Schluss-Tags leerer Elemente entfallen
      if (closing) out += VOID_TAGS.has(tag) ? (tag === "br" ? `<${tag}>` : "") : `</${tag}>`;
      else out += `<${tag}${cleanAttrs(tag, m[3] ?? "")}>`;
    }
    i = lt + m[0].length;
  }
  return out;
}
